import { useParams } from 'react-router-dom'

import dayjs from 'dayjs'

import { usePersonaHealthApi } from '@/common/api/modules/persona'

import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { AXIS_LABELS } from '@/components/container/page/site/persona/axis'

const Bar = ({ percent }: { percent: number }) => (
  <div className='bg-fill-tertiary h-2 w-full overflow-hidden rounded-full'>
    <div className='bg-fill-primary-brand h-full rounded-full' style={{ width: `${percent}%` }} />
  </div>
)

const PersonaHealthPage = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)
  const { data, isLoading } = usePersonaHealthApi({ site_id: siteId })

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='属性ヘルス'
        description='どの軸が判定できて、どの軸が判定できないかを見ます。判定できない軸は「該当者がいない」のではなく「まだ測れていない」状態です。'
      />

      <Content>
        {isLoading || !data ? (
          <Area>
            <AreaContent>
              <Text color={TextColor.SECONDARY}>読み込み中...</Text>
            </AreaContent>
          </Area>
        ) : (
          <>
            {data.alerts.length > 0 && (
              <Area>
                <Heading color={TextColor.CAUTION}>気をつけること</Heading>
                <AreaContent>
                  <ul className='flex flex-col gap-1'>
                    {data.alerts.map((a) => (
                      <li key={a}>
                        <Text size={13} color={TextColor.CAUTION}>
                          ・{a}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </AreaContent>
              </Area>
            )}

            <Area>
              <Heading
                subText={
                  data.computed_at
                    ? `最終計算 ${dayjs(data.computed_at).format('YYYY/MM/DD HH:mm')}`
                    : 'まだ属性計算が実行されていません'
                }
              >
                受信状況
              </Heading>
              <AreaContent>
                <div className='flex flex-wrap gap-x-10 gap-y-4'>
                  <div>
                    <Text size={12} color={TextColor.SECONDARY}>
                      対象顧客
                    </Text>
                    <div>
                      <Text size={24} weight='bold'>
                        {data.customers.toLocaleString()} 人
                      </Text>
                    </div>
                  </div>
                  <div>
                    <Text size={12} color={TextColor.SECONDARY}>
                      付いた属性
                    </Text>
                    <div>
                      <Text size={24} weight='bold'>
                        {data.attributes.toLocaleString()}
                      </Text>
                    </div>
                  </div>
                  <div>
                    <Text size={12} color={TextColor.SECONDARY}>
                      測れるメトリクス
                    </Text>
                    <div>
                      <Text size={24} weight='bold'>
                        {data.metrics_available} / {data.metrics_total}
                      </Text>
                    </div>
                  </div>
                  <div>
                    <Text size={12} color={TextColor.SECONDARY}>
                      名寄せ率（直近30日）
                    </Text>
                    <div>
                      {data.identity_rate === null ? (
                        <Text size={14} color={TextColor.SECONDARY}>
                          行動ログが無く測れない
                        </Text>
                      ) : (
                        <Text size={24} weight='bold'>
                          {Math.round(data.identity_rate * 100)}%
                        </Text>
                      )}
                    </div>
                    {data.identity_visitors !== null && (
                      <div>
                        <Text size={10} color={TextColor.TERTIARY}>
                          {data.identity_resolved?.toLocaleString()} /{' '}
                          {data.identity_visitors?.toLocaleString()} 訪問者
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              </AreaContent>
            </Area>

            <Area>
              <Heading subText='「測れる」は測り方が揃っている数、「属性が付いた顧客」は実際に値が出た人数です。両方見ないと、測り方はあるのにデータが無い状態を見落とします。'>
                軸ごとの判定可能率
              </Heading>
              <AreaContent>
                <div className='flex flex-col gap-2'>
                  {data.axis_coverage.map((c) => (
                    <div key={c.axis} className='flex items-center gap-3'>
                      <div className='w-36 shrink-0'>
                        <Text size={13}>{AXIS_LABELS[c.axis] ?? c.axis}</Text>
                      </div>
                      <div className='w-24 shrink-0 text-right'>
                        <Text
                          size={12}
                          color={c.available === 0 ? TextColor.CRITICAL : TextColor.SECONDARY}
                        >
                          測れる {c.available}/{c.metrics}
                        </Text>
                      </div>
                      <div className='max-w-[420px] min-w-[160px] flex-1'>
                        <Bar percent={Math.round(c.coverage * 100)} />
                      </div>
                      <div className='w-32 shrink-0 text-right'>
                        <Text size={12} color={TextColor.SECONDARY}>
                          {c.customers_with_any.toLocaleString()} 人 ({Math.round(c.coverage * 100)}
                          %)
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </AreaContent>
            </Area>

            {data.blocked_metrics.length > 0 && (
              <Area>
                <Heading subText='ここが埋まると、それを使うペルソナが判定できるようになります。'>
                  算出できないメトリクス
                </Heading>
                <AreaContent>
                  <Table>
                    <thead>
                      <TableRow head>
                        <TableHeadItem>
                          <Text size={12}>メトリクス</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>軸</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>理由</Text>
                        </TableHeadItem>
                      </TableRow>
                    </thead>
                    <tbody>
                      {data.blocked_metrics.map((m) => (
                        <TableRow key={m.metric_key}>
                          <TableRowItem>
                            <Text size={13}>{m.metric_key}</Text>
                          </TableRowItem>
                          <TableRowItem>
                            <Text size={12} color={TextColor.SECONDARY}>
                              {AXIS_LABELS[m.axis ?? ''] ?? m.axis ?? '—'}
                            </Text>
                          </TableRowItem>
                          <TableRowItem>
                            <Text size={12} color={TextColor.SECONDARY}>
                              {m.reason}
                            </Text>
                          </TableRowItem>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </AreaContent>
              </Area>
            )}
          </>
        )}
      </Content>
    </Main>
  )
}

export default PersonaHealthPage
